"use client";

import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Input, inputVariants } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { AvatarUpload } from "@/components/ui/avatar-upload";
import { Select } from "@/components/ui/select";

const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
  "icloud.com", "mail.com", "protonmail.com", "zoho.com", "yandex.com",
  "live.com", "msn.com", "me.com", "mac.com", "hey.com",
]);

const ORG_SIZES = [
  { value: "1-10", label: "1–10 employees" },
  { value: "11-50", label: "11–50 employees" },
  { value: "51-200", label: "51–200 employees" },
  { value: "201-500", label: "201–500 employees" },
  { value: "501-1000", label: "501–1,000 employees" },
  { value: "1001-5000", label: "1,001–5,000 employees" },
  { value: "5001-10000", label: "5,001–10,000 employees" },
  { value: "10000+", label: "10,000+ employees" },
];

export function StepOrganization() {
  const { control, watch, setValue } = useFormContext();
  const prefilled = useRef(false);
  const [orgSizeOpen, setOrgSizeOpen] = useState(false);

  const email = watch("about.email");
  const website = watch("organization.website");
  const logo = watch("organization.logo");

  useEffect(() => {
    if (prefilled.current || website || !email) return;
    const domain = email.split("@")[1]?.toLowerCase();
    if (domain && !PERSONAL_EMAIL_DOMAINS.has(domain)) {
      setValue("organization.website", domain);
      prefilled.current = true;
    }
  }, [email, website, setValue]);

  return (
    <>
      <AvatarUpload
        imageUrl={logo || null}
        onImageChange={(url) => setValue("organization.logo", url)}
        onImageRemove={() => setValue("organization.logo", null)}
        label="Organization Icon"
        optional
        placeholderIcon="IconBag"
        uploadKind="org-logo"
      />

      <FormField
        control={control}
        name="organization.name"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Organization Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="organization.website"
        render={({ field }) => (
          <FormItem className="gap-2">
            <FormLabel optional className="leading-5 font-normal text-heading">
              Organization Website
            </FormLabel>
            <div
              className={cn(
                inputVariants({
                  variant: "default",
                  size: "default",
                  className: "flex shrink-0 items-center font-normal gap-0",
                }),
              )}
            >
              <span className="text-sm text-contrast/40 shrink-0 select-none">
                https://
              </span>
              <FormControl>
                <input
                  type="text"
                  data-slot="input"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    let val = e.target.value;
                    val = val.replace(/^https?:\/\//, "").replace(/^www\./, "");
                    field.onChange(val);
                  }}
                  placeholder="example.com"
                  className="flex-1 min-w-0 bg-transparent py-2 pl-1.5 text-inherit outline-none placeholder:text-contrast/30"
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="organization.size"
        render={({ field }) => {
          const selected = ORG_SIZES.find((o) => o.value === field.value) ?? null;
          return (
            <FormItem>
              <FormLabel required>Organization Size</FormLabel>
              <FormControl>
                <Select.Root open={orgSizeOpen} onOpenChange={setOrgSizeOpen}>
                  <Select.Trigger className="w-full">
                    {selected ? (
                      <span className="min-w-0 flex-1 truncate text-left text-heading">
                        {selected.label}
                      </span>
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-left text-contrast/50">
                        Select...
                      </span>
                    )}
                    <Select.Icon className="ml-auto" />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.List>
                      {ORG_SIZES.map((o) => {
                        const isSelected = o.value === field.value;
                        return (
                          <Select.Item
                            key={o.value}
                            onClick={() => {
                              field.onChange(o.value);
                              setOrgSizeOpen(false);
                            }}
                          >
                            <Select.ItemLabel selected={isSelected}>{o.label}</Select.ItemLabel>
                            {isSelected ? <Select.ItemCheck /> : null}
                          </Select.Item>
                        );
                      })}
                    </Select.List>
                  </Select.Content>
                </Select.Root>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </>
  );
}
